import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Search, Star, Users, Shield, Server, HardDrive,
  Layers, Zap, Clock, CheckCircle2, Info,
  X, Play, ChevronRight, Plug2, Bell, Settings2,
  AlertTriangle, RefreshCw, ArrowRight, Network,
} from 'lucide-react';
import { clsx } from 'clsx';
import api from '../api/client';
import { Blueprint, WorkflowCategory } from '../types';

// ─── Category config ───────────────────────────────────────────────────────────
const CATEGORY_CONFIG: Record<WorkflowCategory, {
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  iconBg: string;
  iconColor: string;
  description: string;
}> = {
  active_directory: {
    label: 'Active Directory',
    icon: Users,
    iconBg: 'bg-blue-600',
    iconColor: 'text-white',
    description: 'User lifecycle, groups, DNS, RBAC',
  },
  security_cyber: {
    label: 'Security & Cyber',
    icon: Shield,
    iconBg: 'bg-red-600',
    iconColor: 'text-white',
    description: 'Threat response, compliance, monitoring',
  },
  virtualization: {
    label: 'Virtualization',
    icon: Server,
    iconBg: 'bg-purple-600',
    iconColor: 'text-white',
    description: 'VMware vSphere, snapshots, DR',
  },
  storage: {
    label: 'Storage',
    icon: HardDrive,
    iconBg: 'bg-orange-600',
    iconColor: 'text-white',
    description: 'NAS, SAN, backup, capacity',
  },
  itsm: {
    label: 'ITSM',
    icon: Layers,
    iconBg: 'bg-cyan-600',
    iconColor: 'text-white',
    description: 'ServiceNow, incidents, change management',
  },
  notifications: {
    label: 'Notifications',
    icon: Bell,
    iconBg: 'bg-yellow-500',
    iconColor: 'text-white',
    description: 'Alerts, email, Slack, Teams',
  },
  custom: {
    label: 'Custom',
    icon: Settings2,
    iconBg: 'bg-slate-600',
    iconColor: 'text-white',
    description: 'Custom automations and scripts',
  },
};

const DIFFICULTY_COLORS: Record<string, string> = {
  beginner:     'text-emerald-400 bg-emerald-400/10 border-emerald-400/20',
  intermediate: 'text-yellow-400 bg-yellow-400/10 border-yellow-400/20',
  advanced:     'text-red-400 bg-red-400/10 border-red-400/20',
};

// ─── Sidebar category item ────────────────────────────────────────────────────
function CategoryItem({
  catKey,
  config,
  selected,
  count,
  onSelect,
  onInfo,
}: {
  catKey: WorkflowCategory;
  config: typeof CATEGORY_CONFIG[WorkflowCategory];
  selected: boolean;
  count?: number;
  onSelect: () => void;
  onInfo: (e: React.MouseEvent) => void;
}) {
  const Icon = config.icon;
  return (
    <div
      onClick={onSelect}
      className={clsx(
        'group flex items-center gap-3 px-3 py-2.5 cursor-pointer transition-all border-b border-gray-200',
        selected
          ? 'bg-orbayx-50 border-l-2 border-l-orbayx-600'
          : 'hover:bg-gray-50 border-l-2 border-l-transparent',
      )}
    >
      <div className={clsx('w-9 h-9 rounded flex items-center justify-center flex-shrink-0', config.iconBg)}>
        <Icon className={clsx("w-5 h-5", config.iconColor)} />
      </div>
      <div className="flex-1 min-w-0">
        <p className={clsx('text-sm font-medium truncate', selected ? 'text-orbayx-700' : 'text-gray-600')}>
          {config.label}
        </p>
        {count !== undefined && (
          <p className="text-[10px] text-gray-400">{count} blueprint{count !== 1 ? 's' : ''}</p>
        )}
      </div>
      <button
        onClick={onInfo}
        className="opacity-0 group-hover:opacity-100 p-1 rounded hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-all flex-shrink-0"
        title={config.description}
      >
        <Info className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}

// ─── Blueprint Card (vRA style) ────────────────────────────────────────────────
function BlueprintCard({
  blueprint,
  onSelect,
  onDeploy,
}: {
  blueprint: Blueprint;
  onSelect: (bp: Blueprint) => void;
  onDeploy: (bp: Blueprint) => void;
}) {
  const cat = CATEGORY_CONFIG[blueprint.category];
  const Icon = cat?.icon || Layers;

  return (
    <div className="bg-gray-50 border border-gray-200 rounded-lg overflow-hidden flex flex-col hover:border-orbayx-500/40 hover:shadow-md hover:shadow-gray-300 transition-all duration-200">
      {/* Card body */}
      <div
        className="flex gap-3 p-4 flex-1 cursor-pointer"
        onClick={() => onSelect(blueprint)}
      >
        {/* Icon */}
        <div
          className={clsx(
            'w-14 h-14 rounded-lg flex items-center justify-center flex-shrink-0',
            cat?.iconBg,
          )}
        >
          <Icon className="w-7 h-7 text-white" />
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <button
            className="text-sm font-semibold text-orbayx-600 hover:text-orbayx-700 hover:underline transition-colors text-left leading-snug mb-0.5 w-full"
            onClick={(e) => { e.stopPropagation(); onSelect(blueprint); }}
          >
            {blueprint.name}
          </button>
          <p className="text-[11px] text-gray-400 mb-1.5">
            By Orbayx · <span className="text-gray-400">{cat?.label}</span>
          </p>
          <p className="text-[11px] text-gray-500 leading-relaxed line-clamp-3">
            {blueprint.description}
          </p>
        </div>
      </div>

      {/* Card footer with Deploy button */}
      <div className="flex items-center justify-between px-4 py-2.5 border-t border-gray-200 bg-gray-50">
        <div className="flex items-center gap-2 text-[10px] text-gray-400">
          <Clock className="w-3 h-3" />
          <span>{blueprint.estimatedDuration}</span>
        </div>
        <button
          onClick={() => onDeploy(blueprint)}
          className="px-3.5 py-1.5 bg-orbayx-600 hover:bg-orbayx-500 rounded text-xs font-semibold text-white transition-all shadow-sm shadow-orbayx-900/50"
        >
          Deploy
        </button>
      </div>
    </div>
  );
}

// ─── Detail Modal (Info panel) ────────────────────────────────────────────────
function BlueprintModal({
  blueprint,
  onClose,
  onDeploy,
}: {
  blueprint: Blueprint;
  onClose: () => void;
  onDeploy: (bp: Blueprint) => void;
}) {
  const cat = CATEGORY_CONFIG[blueprint.category];
  const Icon = cat?.icon || Layers;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" onClick={onClose}>
      <div
        className="bg-white border border-gray-200 rounded-xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Modal header */}
        <div className="flex items-center gap-4 px-6 py-5 border-b border-gray-200 bg-gray-50">
          <div className={clsx('w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0', cat?.iconBg)}>
            <Icon className="w-6 h-6 text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="text-base font-bold text-white">{blueprint.name}</h2>
            <p className="text-xs text-gray-400 mt-0.5">
              By Orbayx &nbsp;·&nbsp; {cat?.label} &nbsp;·&nbsp; v{blueprint.version}
              &nbsp;·&nbsp;
              <span className="text-yellow-400">★ {blueprint.rating}</span>
              &nbsp;·&nbsp;
              <span>{blueprint.usageCount.toLocaleString()} uses</span>
            </p>
          </div>
          <span className={clsx('text-xs px-2 py-0.5 rounded border', DIFFICULTY_COLORS[blueprint.difficulty])}>
            {blueprint.difficulty}
          </span>
          <button onClick={onClose} className="p-1.5 hover:bg-gray-100 rounded-lg text-gray-400 hover:text-gray-900 transition-colors">
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Modal body */}
        <div className="flex-1 overflow-y-auto">
          {/* Description */}
          <div className="px-6 py-5 border-b border-gray-200">
            <p className="text-sm text-gray-600 leading-relaxed">{blueprint.description}</p>
          </div>

          <div className="px-6 py-5 space-y-6">
            {/* Workflow steps */}
            <div>
              <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Workflow Steps</h3>
              <div className="flex flex-wrap items-center gap-1">
                {blueprint.definition.nodes.map((node, i) => (
                  <React.Fragment key={node.id}>
                    <span className="px-2 py-1 bg-gray-50 border border-gray-200 rounded text-[11px] text-gray-500 max-w-[160px] truncate" title={node.label}>
                      {node.label}
                    </span>
                    {i < blueprint.definition.nodes.length - 1 && (
                      <ArrowRight className="w-3 h-3 text-gray-300 flex-shrink-0" />
                    )}
                  </React.Fragment>
                ))}
              </div>
            </div>

            {/* Benefits */}
            <div>
              <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Key Benefits</h3>
              <ul className="space-y-2">
                {blueprint.benefits.map((b) => (
                  <li key={b} className="flex items-start gap-2 text-xs text-gray-600">
                    <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400 flex-shrink-0 mt-0.5" />
                    {b}
                  </li>
                ))}
              </ul>
            </div>

            {/* Use Cases */}
            <div>
              <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Use Cases</h3>
              <div className="flex flex-wrap gap-1.5">
                {blueprint.useCases.map((uc) => (
                  <span key={uc} className="px-2.5 py-1 bg-blue-500/10 border border-blue-500/20 rounded-lg text-[11px] text-blue-300">{uc}</span>
                ))}
              </div>
            </div>

            {/* Integrations */}
            <div>
              <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Required Integrations</h3>
              <div className="flex flex-wrap gap-1.5">
                {blueprint.requiredIntegrations.map((int) => (
                  <span key={int} className="flex items-center gap-1.5 px-2.5 py-1 bg-purple-500/10 border border-purple-500/20 rounded-lg text-[11px] text-purple-300">
                    <Plug2 className="w-3 h-3" />
                    {int.replace(/_/g, ' ')}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Modal footer */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200 bg-gray-50">
          <span className="text-xs text-gray-400">
            ~{blueprint.estimatedDuration} &nbsp;·&nbsp; {blueprint.definition.nodes.length} steps
          </span>
          <div className="flex gap-2">
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-50 hover:bg-gray-100 border border-gray-200 rounded-lg text-sm text-gray-600 hover:text-gray-900 transition-all"
            >
              Cancel
            </button>
            <button
              onClick={() => onDeploy(blueprint)}
              className="flex items-center gap-2 px-5 py-2 bg-orbayx-600 hover:bg-orbayx-500 rounded-lg text-sm text-white font-semibold transition-all"
            >
              <Play className="w-4 h-4" />
              Deploy Blueprint
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Main page ─────────────────────────────────────────────────────────────────
export function Blueprints() {
  const navigate = useNavigate();
  const [selectedCategory, setSelectedCategory] = useState<WorkflowCategory | ''>('');
  const [search, setSearch] = useState('');
  const [selectedBlueprint, setSelectedBlueprint] = useState<Blueprint | null>(null);
  const [tooltipCat, setTooltipCat] = useState<WorkflowCategory | null>(null);

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['blueprints', search, selectedCategory],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (search) params.set('search', search);
      if (selectedCategory) params.set('category', selectedCategory);
      params.set('limit', '100');
      params.set('sortBy', 'usageCount');
      const res = await api.get(`/blueprints?${params}`);
      return res.data.data as { data: Blueprint[]; total: number };
    },
    staleTime: 60000,
  });

  const { data: statsData } = useQuery({
    queryKey: ['blueprint-stats'],
    queryFn: async () => {
      const res = await api.get('/blueprints/stats');
      return res.data.data as {
        total: number;
        byCategory: Record<string, number>;
        totalUsage: number;
        avgRating: number;
      };
    },
    staleTime: 60000,
  });

  const blueprints: Blueprint[] = data?.data || [];
  const totalCount = data?.total ?? 0;

  const handleDeploy = (bp: Blueprint) => {
    navigate(`/builder?blueprint=${bp.id}`);
  };

  const categories = Object.entries(CATEGORY_CONFIG) as [WorkflowCategory, typeof CATEGORY_CONFIG[WorkflowCategory]][];

  const headerTitle = selectedCategory
    ? `${CATEGORY_CONFIG[selectedCategory]?.label} (${totalCount})`
    : `All Blueprints (${statsData?.total ?? totalCount})`;

  return (
    <div className="flex h-full min-h-screen bg-[#f0f2f5]">

      {/* ── Left sidebar ───────────────────────────────────── */}
      <aside className="w-52 flex-shrink-0 bg-white border-r border-gray-200 flex flex-col">

        {/* Sidebar title */}
        <div className="px-4 py-3 border-b border-gray-200">
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-widest">Catalog</p>
        </div>

        {/* All blueprints */}
        <div
          onClick={() => setSelectedCategory('')}
          className={clsx(
            'flex items-center gap-3 px-3 py-3 cursor-pointer border-b border-gray-200 transition-all',
            selectedCategory === ''
              ? 'bg-orbayx-50 border-l-2 border-l-orbayx-600'
              : 'hover:bg-gray-50 border-l-2 border-l-transparent',
          )}
        >
          <div className="w-9 h-9 rounded flex items-center justify-center flex-shrink-0 bg-orbayx-600">
            <Layers className="w-5 h-5 text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <p className={clsx('text-sm font-semibold', selectedCategory === '' ? 'text-orbayx-700' : 'text-gray-700')}>
              All Services
            </p>
            {statsData && (
              <p className="text-[10px] text-gray-400">{statsData.total} blueprints</p>
            )}
          </div>
        </div>

        {/* Category list */}
        {categories.map(([key, config]) => (
          <CategoryItem
            key={key}
            catKey={key}
            config={config}
            selected={selectedCategory === key}
            count={statsData?.byCategory?.[key]}
            onSelect={() => setSelectedCategory(key === selectedCategory ? '' : key)}
            onInfo={(e) => {
              e.stopPropagation();
              setTooltipCat(tooltipCat === key ? null : key);
            }}
          />
        ))}

        {/* Stats footer */}
        {statsData && (
          <div className="mt-auto px-3 py-3 border-t border-gray-200 space-y-2">
            <div className="flex justify-between text-[11px]">
              <span className="text-gray-400">Total deployed</span>
              <span className="text-gray-600 font-medium">{statsData.totalUsage?.toLocaleString()}</span>
            </div>
            <div className="flex justify-between text-[11px]">
              <span className="text-gray-400">Avg. rating</span>
              <span className="text-yellow-400 font-medium">★ {statsData.avgRating}</span>
            </div>
          </div>
        )}
      </aside>

      {/* ── Main content ───────────────────────────────────── */}
      <div className="flex-1 flex flex-col overflow-hidden">

        {/* Catalog header bar (vRA style) */}
        <div className="flex items-center justify-between px-5 py-2.5 bg-gray-50 border-b border-gray-200">
          <h2 className="text-sm font-semibold text-gray-700">{headerTitle}</h2>

          <div className="flex items-center gap-3">
            {/* Team / Group filter label */}
            <div className="flex items-center gap-2 text-xs text-gray-400">
              <span>Business group:</span>
              <select className="bg-[#f0f2f5] border border-gray-300 rounded px-2 py-1 text-gray-600 text-xs focus:outline-none focus:border-orbayx-500">
                <option>All</option>
                <option>IT Operations</option>
                <option>Security</option>
                <option>Infrastructure</option>
              </select>
            </div>

            {/* Search */}
            <div className="relative">
              <input
                type="text"
                placeholder="Search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-44 bg-[#f0f2f5] border border-gray-300 rounded px-3 py-1.5 pr-7 text-xs text-white placeholder-white/30 focus:outline-none focus:border-orbayx-500 transition-all"
              />
              {search ? (
                <button onClick={() => setSearch('')} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-900">
                  <X className="w-3 h-3" />
                </button>
              ) : (
                <Search className="absolute right-2 top-1/2 -translate-y-1/2 w-3 h-3 text-gray-400" />
              )}
            </div>
          </div>
        </div>

        {/* Cards grid */}
        <div className="flex-1 overflow-y-auto p-5">
          {isLoading ? (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {Array.from({ length: 12 }).map((_, i) => (
                <div key={i} className="bg-gray-50 border border-gray-200 rounded-lg p-4 animate-pulse h-40">
                  <div className="flex gap-3">
                    <div className="w-14 h-14 bg-gray-50 rounded-lg flex-shrink-0" />
                    <div className="flex-1 space-y-2">
                      <div className="h-3 bg-gray-50 rounded w-3/4" />
                      <div className="h-2 bg-gray-50 rounded w-1/2" />
                      <div className="h-2 bg-gray-50 rounded w-full" />
                      <div className="h-2 bg-gray-50 rounded w-5/6" />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : isError ? (
            <div className="flex flex-col items-center justify-center h-64 text-center">
              <div className="w-12 h-12 bg-red-500/10 border border-red-500/20 rounded-xl flex items-center justify-center mb-3">
                <AlertTriangle className="w-5 h-5 text-red-400" />
              </div>
              <p className="text-sm text-gray-500 font-medium mb-1">Failed to load blueprints</p>
              <p className="text-xs text-gray-400 mb-4">Check your connection and try again</p>
              <button
                onClick={() => refetch()}
                className="flex items-center gap-1.5 px-4 py-2 bg-gray-50 hover:bg-gray-100 border border-gray-200 rounded-lg text-sm text-gray-600 hover:text-gray-900 transition-all"
              >
                <RefreshCw className="w-3.5 h-3.5" /> Retry
              </button>
            </div>
          ) : blueprints.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 text-center">
              <div className="w-12 h-12 bg-gray-50 border border-gray-200 rounded-xl flex items-center justify-center mb-3">
                <Layers className="w-5 h-5 text-gray-300" />
              </div>
              <p className="text-sm text-gray-500">No blueprints match this filter</p>
              <button
                onClick={() => { setSearch(''); setSelectedCategory(''); }}
                className="mt-3 text-xs text-orbayx-600 hover:text-orbayx-700 transition-colors"
              >
                Clear filters
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {blueprints.map((bp) => (
                <BlueprintCard
                  key={bp.id}
                  blueprint={bp}
                  onSelect={setSelectedBlueprint}
                  onDeploy={handleDeploy}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* ── Info tooltip (category description) ──────────────── */}
      {tooltipCat && (
        <div
          className="fixed bottom-6 left-56 z-50 w-64 p-3 bg-white border border-gray-300 rounded-xl shadow-xl text-xs text-gray-600"
          onClick={() => setTooltipCat(null)}
        >
          <div className="flex items-start gap-2">
            <Info className="w-3.5 h-3.5 text-orbayx-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-semibold text-white mb-0.5">{CATEGORY_CONFIG[tooltipCat]?.label}</p>
              <p>{CATEGORY_CONFIG[tooltipCat]?.description}</p>
            </div>
          </div>
        </div>
      )}

      {/* ── Detail modal ───────────────────────────────────── */}
      {selectedBlueprint && (
        <BlueprintModal
          blueprint={selectedBlueprint}
          onClose={() => setSelectedBlueprint(null)}
          onDeploy={(bp) => { setSelectedBlueprint(null); handleDeploy(bp); }}
        />
      )}
    </div>
  );
}
